import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import Nav from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSeoTitle } from "@/lib/useSeoTitle";

const AMBIENT = "https://images.pexels.com/photos/8564636/pexels-photo-8564636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const TRIAL_ID_KEY = "saga_trial_id";
const TRIAL_ARCHETYPE_KEY = "saga_trial_archetype";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  useSeoTitle({
    title: "Log In to TalesTalesTales",
    description: "Log in to continue your TalesTalesTales AI Novel saga, access saved campaigns, and manage your account.",
    path: "/login",
  });
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const fromTrial = params.get("from") === "trial";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, traveller.");
      // Trial continuity for returning users — if they came in from the
      // trial flow, claim the trial into THIS account before navigating.
      const trialId = (params.get("trial_id") || localStorage.getItem(TRIAL_ID_KEY) || "").trim();
      if (trialId) {
        try {
          const r = await api.post("/trial/claim", { trial_id: trialId });
          if (r.data?.campaign_id) {
            localStorage.removeItem(TRIAL_ID_KEY);
            localStorage.removeItem(TRIAL_ARCHETYPE_KEY);
            toast.success(r.data.already_claimed
              ? "Trial saga restored."
              : "Trial saga bound to your account.");
            nav(`/play/${r.data.campaign_id}`);
            return;
          }
        } catch (claimErr) {
          // Conflict (someone else already claimed it) or 404 — surface but
          // don't block the login flow.
          const msg = claimErr?.response?.data?.detail;
          if (msg) toast.error(msg);
        }
      }
      if (fromTrial) {
        nav("/pricing");
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The gate refuses you.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#0f0d11]">
      <Nav />
      <div id="main-content" className="relative min-h-[calc(100vh-72px)] flex items-center justify-center px-6">
        <div className="absolute inset-0 opacity-40">
          <img src={AMBIENT} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0d11]/70 via-[#0f0d11]/85 to-[#0f0d11]" />
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="relative w-full max-w-md parchment gold-border rounded-xl p-10 candle-glow rise"
        >
          <Flame className="w-8 h-8 text-amber-400 flicker mb-3" />
          <h1 className="font-display text-4xl text-amber-50 mb-2">Return to your saga</h1>
          <p className="font-body text-amber-100/60 mb-8">Sign in to continue your tale.</p>

          <div className="space-y-5">
            <div>
              <Label className="font-ui text-amber-200/80 text-xs tracking-widest uppercase">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                placeholder="ranger@saga.tales"
              />
            </div>
            <div>
              <Label className="font-ui text-amber-200/80 text-xs tracking-widest uppercase">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#15121a] border-amber-700/30 text-amber-100 font-body focus-visible:ring-amber-500"
                placeholder="••••••••"
              />
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit" disabled={busy}
              className="w-full h-12 bg-amber-600 hover:bg-amber-500 text-stone-900 font-ui candle-glow"
            >
              {busy ? "Opening the gate..." : "Enter"}
            </Button>
          </div>

          <p className="mt-6 text-center font-body text-amber-200/60 text-sm">
            New to these lands?{" "}
            <Link data-testid="login-to-register-link" to="/register" className="text-amber-400 hover:text-amber-300">
              Forge a hero
            </Link>
          </p>
          <p className="mt-2 text-center font-body text-amber-200/50 text-xs">
            <Link data-testid="login-to-forgot-link" to="/forgot" className="hover:text-amber-300">
              Forgot password?
            </Link>
          </p>
        </form>
      </div>
      <section className="max-w-3xl mx-auto px-6 py-10 text-center">
        <h2 className="font-display text-2xl text-amber-100 mb-3">Welcome back to TalesTalesTales</h2>
        <p className="font-body text-amber-100/65 text-sm leading-relaxed">
          Sign in to continue your AI Novel saga, write the next chapter of your AI fantasy novel,
          or pick up a story with light D&amp;D-style RPG elements where you left it. The Memory Codex helps your characters,
          party, and visited realms stay consistent as you continue. New here? <Link to="/register" className="gold-text hover:text-amber-200">Forge a hero</Link>{" "}
          and claim your 10 free turns each day, or <Link to="/worlds" className="gold-text hover:text-amber-200">browse community realms</Link> for inspiration.
          If you cannot recall your password, the <Link to="/forgot" className="gold-text hover:text-amber-200">password recovery scroll</Link> can help —
          you set a security question when you signed up, and your answer will reopen the gate.
        </p>
      </section>
    </div>
  );
}
